import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type MouseEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import {
  ChevronDownIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  EyeSlashIcon,
  PencilIcon,
} from '@heroicons/react/24/outline'
import {
  applyLedgerFilters,
  buildLedgerBkTxnIdOptions,
  buildLedgerBsLogIdOptions,
  buildLedgerKindOptions,
  buildLedgerLinkedInvoiceOptions,
  buildLedgerOrderNumberOptions,
  buildLedgerProductOptions,
  EMPTY_LEDGER_TABLE_FILTERS,
  hasActiveLedgerTableFilters,
  isLedgerFieldLocked,
  LEDGER_KIND_OPTIONS,
  LEDGER_STORE_LOCATIONS,
  LEDGER_SIGNED_CASES_FILTER_OPTIONS,
  LEDGER_STATUS_FILTER_OPTIONS,
  LEDGER_STATUS_LABELS,
  ledgerKindLabel,
  ledgerProductLabel,
  ledgerRowGreyStatusIds,
  ledgerRowIgnoredRedStatusIds,
  ledgerRowRedStatusIds,
  ledgerSortValue,
  migrateLedgerTableFilters,
  productForSku,
  type LedgerSignedCasesFilterId,
  type LedgerStatusFilterId,
  type LedgerTableFilters,
} from '@/components/ledgerFilters'
import { hasActiveAmountFilter, TABLE_AMOUNT_FILTER_OPS, type TableAmountFilterOp } from '@/components/tableFilterUtils'
import MultiSelectFilter from '@/components/MultiSelectFilter'
import DateInput from '@/components/DateInput'
import { formatUnitCostDisplay, resolveLedgerMoneyCommit } from '@/components/ledgerMoney'
import WarehouseLedgerFlagsModal from '@/components/WarehouseLedgerFlagsModal'
import WarehouseLedgerOverrideModal from '@/components/WarehouseLedgerOverrideModal'
import {
  isPinnedLedgerColumn,
  migrateWarehouseLedgerColumnWidths,
  pinnedLedgerCellClass,
  pinnedLedgerHeaderClass,
  WAREHOUSE_LEDGER_COLUMN_KEYS,
  WAREHOUSE_LEDGER_DEFAULT_WIDTHS,
  WAREHOUSE_LEDGER_PAGE_KEY,
  WAREHOUSE_LEDGER_PINNED_COLUMNS,
} from '@/components/warehouseLedgerTableConfig'
import { useSortableTable } from '@/hooks/useSortableTable'
import type { SortDirection } from '@/hooks/useSortableTable'
import type { WarehouseLedgerKind, WarehouseLedgerTransactionItem } from '@/services/warehouseTypes'
import { exportWarehouseLedgerSheet } from '@/services/warehouse'
import { toast } from '@/store/toastStore'
import { ColumnResizeHandle, HoverHelp, TableToolbar, useColumnWidths } from '@/tableControls'
import { formatApiError } from '@/utils/apiError'
import { normalizeBulkSku } from '@/utils/normalizeBulkSku'
import { downloadCsvFile, fixedColumnStyle } from '@/utils/warehouseTableHelpers'
import {
  loadFilterCache,
  saveFilterCache,
  WAREHOUSE_LEDGER_FILTER_CACHE_KEY,
} from '@/utils/tableFilterCache'

export type EditableLedgerRow = WarehouseLedgerTransactionItem & {
  dirty: boolean
}

function LedgerSortableHeader({
  label,
  sortKey,
  sortDirection,
  onSort,
  className = '',
  title,
  columnKey,
  onStartResize,
  style,
}: {
  label: ReactNode
  sortKey: string
  sortDirection: SortDirection | null
  onSort: (key: string) => void
  className?: string
  title?: string
  columnKey: string
  onStartResize: (key: string, event: ReactMouseEvent) => void
  style?: CSSProperties
}) {
  const isSorted = sortDirection !== null

  return (
    <th
      className={`sortable relative ${isSorted ? 'sorted' : ''} ${className}`}
      style={style}
      title={title}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1 pr-2">
        {label}
        <span className="sort-indicator">
          {sortDirection === 'asc' ? (
            <ChevronUpIcon className="h-3 w-3" />
          ) : sortDirection === 'desc' ? (
            <ChevronDownIcon className="h-3 w-3" />
          ) : (
            <ChevronUpDownIcon className="h-3 w-3" />
          )}
        </span>
      </span>
      <ColumnResizeHandle columnKey={columnKey} onStartResize={onStartResize} />
    </th>
  )
}

function cellInputClass(locked: boolean, highlightEmpty = false): string {
  return `input box-border h-9 w-full min-w-0 px-2 text-sm leading-normal ${
    locked ? 'cursor-not-allowed bg-gray-50 text-gray-500' : ''
  } ${highlightEmpty ? 'bg-amber-50 ring-2 ring-amber-300' : ''}`
}

const LEDGER_READONLY_CELL = 'flex h-9 w-full items-center'

function escapeCsvCell(value: string): string {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function ledgerExportFlags(row: WarehouseLedgerTransactionItem): string {
  const labels = [
    ...ledgerRowGreyStatusIds(row).map((id) => LEDGER_STATUS_LABELS[id]),
    ...ledgerRowRedStatusIds(row).map((id) => LEDGER_STATUS_LABELS[id]),
    ...ledgerRowIgnoredRedStatusIds(row).map((id) => LEDGER_STATUS_LABELS[id]),
  ]
  return labels.join('; ')
}

function LedgerMoneyInput({
  value,
  busy,
  locked = false,
  emptyClears = false,
  onCommit,
}: {
  value: number | null
  busy: boolean
  locked?: boolean
  emptyClears?: boolean
  onCommit: (next: number | null) => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')

  function commit() {
    const result = resolveLedgerMoneyCommit(draft, value, { emptyClears })
    if (result.action === 'commit') {
      onCommit(result.value)
    }
    setEditing(false)
  }

  return (
    <input
      className={`${cellInputClass(locked)} tabular-nums`}
      value={editing ? draft : formatUnitCostDisplay(value)}
      inputMode="decimal"
      disabled={busy || locked}
      readOnly={locked}
      placeholder="$0.00"
      onFocus={() => {
        if (locked) return
        setDraft(value == null ? '' : formatUnitCostDisplay(value))
        setEditing(true)
      }}
      onChange={(e) => {
        setEditing(true)
        setDraft(e.target.value)
      }}
      onBlur={() => {
        if (editing) commit()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          commit()
        }
      }}
    />
  )
}

function FindableSelect({
  className,
  value,
  disabled,
  'aria-label': ariaLabel,
  findText,
  onChange,
  children,
}: {
  className?: string
  value: string
  disabled?: boolean
  'aria-label'?: string
  findText: string
  onChange: (event: ChangeEvent<HTMLSelectElement>) => void
  children: ReactNode
}) {
  return (
    <div className="relative h-9 w-full">
      <select
        className={className}
        value={value}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={onChange}
      >
        {children}
      </select>
      {findText ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 flex items-center overflow-hidden whitespace-nowrap px-2 text-sm text-transparent"
        >
          {findText}
        </span>
      ) : null}
    </div>
  )
}

function LedgerStatusChip({
  row,
  onOpenFlags,
  onOpenOverride,
}: {
  row: WarehouseLedgerTransactionItem
  onOpenFlags: () => void
  onOpenOverride: () => void
}) {
  const greyIds = ledgerRowGreyStatusIds(row)
  const redIds = ledgerRowRedStatusIds(row)
  const ignoredRedIds = ledgerRowIgnoredRedStatusIds(row)
  const showRedButton = redIds.length > 0
  const showReviewedButton = redIds.length === 0 && ignoredRedIds.length > 0

  if (greyIds.length === 0 && !showRedButton && !showReviewedButton) return null

  const greyLabels = greyIds.map((id) => LEDGER_STATUS_LABELS[id])
  const redLabels = redIds.map((id) => LEDGER_STATUS_LABELS[id])
  const reviewedLabels = ignoredRedIds.map((id) => LEDGER_STATUS_LABELS[id])

  function handleOpenFlags(event: MouseEvent) {
    event.stopPropagation()
    onOpenFlags()
  }

  function handleOpenOverride(event: MouseEvent) {
    event.stopPropagation()
    onOpenOverride()
  }

  return (
    <div className="flex items-center gap-1">
      {greyIds.length > 0 ? (
        <HoverHelp
          aria-label={greyLabels.join(', ')}
          label={
            <button
              type="button"
              className="inline-flex rounded text-gray-400 hover:bg-gray-100"
              aria-label={`Override details: ${greyLabels.join(', ')}`}
              onClick={handleOpenOverride}
            >
              <PencilIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          }
        >
          <ul className="list-disc space-y-1 pl-4">
            {greyLabels.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
        </HoverHelp>
      ) : null}
      {showRedButton ? (
        <HoverHelp
          aria-label={redLabels.join(', ')}
          label={
            <button
              type="button"
              className="inline-flex rounded text-red-600 hover:bg-red-50"
              aria-label={`Data-quality flags: ${redLabels.join(', ')}`}
              onClick={handleOpenFlags}
            >
              <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          }
        >
          <ul className="list-disc space-y-1 pl-4">
            {redLabels.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
        </HoverHelp>
      ) : null}
      {showReviewedButton ? (
        <HoverHelp
          aria-label={`Reviewed flags: ${reviewedLabels.join(', ')}`}
          label={
            <button
              type="button"
              className="inline-flex rounded text-gray-400 hover:bg-gray-100"
              aria-label="Reviewed data-quality flags"
              onClick={handleOpenFlags}
            >
              <EyeSlashIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          }
        >
          <ul className="list-disc space-y-1 pl-4">
            {reviewedLabels.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
        </HoverHelp>
      ) : null}
    </div>
  )
}

const VOID_LEDGER_CONFIRM_MESSAGE =
  'Void this transaction? It will stay visible but stop counting toward stock, flow, allocation, and journal entries.'

type LedgerRowContextMenuState = {
  bkTxnId: string
  x: number
  y: number
}

function clampContextMenuPosition(
  x: number,
  y: number,
  width: number,
  height: number
): { x: number; y: number } {
  const padding = 8
  const maxX = Math.max(padding, window.innerWidth - width - padding)
  const maxY = Math.max(padding, window.innerHeight - height - padding)
  return {
    x: Math.min(Math.max(x, padding), maxX),
    y: Math.min(Math.max(y, padding), maxY),
  }
}

function LedgerRowContextMenu({
  row,
  position,
  busy,
  onClose,
  onVoidRow,
  onUnvoidRow,
}: {
  row: WarehouseLedgerTransactionItem
  position: { x: number; y: number }
  busy: boolean
  onClose: () => void
  onVoidRow?: (bkTxnId: string) => void
  onUnvoidRow?: (bkTxnId: string) => void
}) {
  const menuRef = useRef<HTMLDivElement>(null)
  const [clampedPosition, setClampedPosition] = useState(position)

  useLayoutEffect(() => {
    const menu = menuRef.current
    if (!menu) {
      setClampedPosition(position)
      return
    }
    const rect = menu.getBoundingClientRect()
    setClampedPosition(clampContextMenuPosition(position.x, position.y, rect.width, rect.height))
  }, [position.x, position.y])

  useEffect(() => {
    function handlePointerDown(event: Event) {
      const target = event.target
      if (target instanceof Node && menuRef.current?.contains(target)) return
      onClose()
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }

    function handleScroll() {
      onClose()
    }

    window.addEventListener('mousedown', handlePointerDown)
    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('scroll', handleScroll, true)
    return () => {
      window.removeEventListener('mousedown', handlePointerDown)
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [onClose])

  const isVoided = row.voided_at != null

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      className="fixed z-50 min-w-[11rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg"
      style={{ left: clampedPosition.x, top: clampedPosition.y }}
    >
      {isVoided ? (
        <button
          type="button"
          role="menuitem"
          className="block w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={busy || !onUnvoidRow}
          onClick={() => {
            onClose()
            onUnvoidRow?.(row.bk_txn_id)
          }}
        >
          Unvoid transaction
        </button>
      ) : (
        <button
          type="button"
          role="menuitem"
          className="block w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={busy || !onVoidRow}
          onClick={() => {
            onClose()
            if (!onVoidRow) return
            if (window.confirm(VOID_LEDGER_CONFIRM_MESSAGE)) {
              onVoidRow(row.bk_txn_id)
            }
          }}
        >
          Void transaction…
        </button>
      )}
    </div>,
    document.body
  )
}

function formatSignedCasesDisplay(value: number): string {
  const amount = Number(value) || 0
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function SignedCasesInput({
  value,
  busy,
  onCommit,
}: {
  value: number
  busy: boolean
  onCommit: (next: number) => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const signedCases = Number(value) || 0

  function commit() {
    const next = Number(draft.replace(/,/g, ''))
    if (Number.isFinite(next)) {
      onCommit(next)
    }
    setEditing(false)
  }

  return (
    <input
      className={`${cellInputClass(false)} tabular-nums ${
        signedCases < 0
          ? 'font-medium text-red-700'
          : signedCases > 0
            ? 'font-medium text-emerald-700'
            : ''
      }`}
      value={editing ? draft : formatSignedCasesDisplay(signedCases)}
      inputMode="decimal"
      disabled={busy}
      onFocus={() => {
        setDraft(String(signedCases))
        setEditing(true)
      }}
      onChange={(e) => {
        setEditing(true)
        setDraft(e.target.value)
      }}
      onBlur={() => {
        if (editing) commit()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          commit()
        }
      }}
    />
  )
}

function LinkedInvoiceCell({
  row,
  invoiceNumberByLineId,
  onJumpToProductInvoice,
}: {
  row: WarehouseLedgerTransactionItem
  invoiceNumberByLineId?: Map<number, string>
  onJumpToProductInvoice?: (productInvoiceLineId: number) => void
}) {
  if (row.kind === 'initial_store_transfer') {
    return (
      <div className={LEDGER_READONLY_CELL}>
        <span className="text-xs text-gray-400">No invoice expected</span>
      </div>
    )
  }

  if (row.product_invoice_line_id != null) {
    const invoiceNumber =
      invoiceNumberByLineId?.get(row.product_invoice_line_id) ??
      String(row.product_invoice_line_id)
    return (
      <div className={LEDGER_READONLY_CELL}>
        <button
          type="button"
          className="inline-flex max-w-full min-w-0 items-center rounded bg-emerald-50 px-2 py-0.5 text-xs text-emerald-800 ring-1 ring-emerald-100 hover:bg-emerald-100"
          title={`Open product invoice ${invoiceNumber}`}
          onClick={(e) => {
            e.stopPropagation()
            onJumpToProductInvoice?.(row.product_invoice_line_id!)
          }}
        >
          <span className="min-w-0 truncate">{invoiceNumber}</span>
        </button>
      </div>
    )
  }

  return (
    <div className={LEDGER_READONLY_CELL}>
      <span className="text-xs text-gray-400">—</span>
    </div>
  )
}

type LedgerFiltersBarProps = {
  rows: EditableLedgerRow[]
  filters: LedgerTableFilters
  hasActiveFilters: boolean
  invoiceNumberByLineId?: Map<number, string>
  skuDisplayNames?: ReadonlyMap<string, string>
  onFilterChange: <K extends keyof LedgerTableFilters>(key: K, value: LedgerTableFilters[K]) => void
  onClearFilters: () => void
  getFilterInputClass: (value: string) => string
}

function LedgerFiltersBar({
  rows,
  filters,
  hasActiveFilters,
  invoiceNumberByLineId,
  skuDisplayNames,
  onFilterChange,
  onClearFilters,
  getFilterInputClass,
}: LedgerFiltersBarProps) {
  const productOptions = useMemo(
    () => buildLedgerProductOptions(rows, skuDisplayNames),
    [rows, skuDisplayNames]
  )
  const kindOptions = useMemo(() => buildLedgerKindOptions(rows), [rows])
  const bkTxnOptions = useMemo(() => buildLedgerBkTxnIdOptions(rows), [rows])
  const bsLogOptions = useMemo(() => buildLedgerBsLogIdOptions(rows), [rows])
  const orderOptions = useMemo(() => buildLedgerOrderNumberOptions(rows), [rows])
  const linkedInvoiceOptions = useMemo(
    () => buildLedgerLinkedInvoiceOptions(rows, invoiceNumberByLineId),
    [rows, invoiceNumberByLineId]
  )
  const hasActiveUnitCostFilter = hasActiveAmountFilter(filters.unit_cost, filters.unit_cost_op)

  return (
    <div className="mt-4 shrink-0 space-y-3 rounded-lg border border-gray-100 bg-gray-50/80 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Filters</span>
        {hasActiveFilters ? (
          <button
            type="button"
            className="text-xs font-medium text-brand-700 hover:underline"
            onClick={onClearFilters}
          >
            Clear all
          </button>
        ) : null}
      </div>
      <div className="grid grid-cols-2 items-start gap-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        <MultiSelectFilter
          id="ledger-filter-flags"
          label="Flags"
          options={LEDGER_STATUS_FILTER_OPTIONS}
          selected={filters.status}
          onChange={(value) => onFilterChange('status', value as LedgerStatusFilterId[])}
        />
        <MultiSelectFilter
          id="ledger-filter-bk-txn-id"
          label="BK Txn ID"
          options={bkTxnOptions}
          selected={filters.bk_txn_id}
          onChange={(value) => onFilterChange('bk_txn_id', value)}
          searchable
        />
        <MultiSelectFilter
          id="ledger-filter-bs-log-id"
          label="BS Log ID"
          options={bsLogOptions}
          selected={filters.bs_log_id}
          onChange={(value) => onFilterChange('bs_log_id', value)}
          searchable
        />
        <div className="flex flex-col gap-1">
          <label htmlFor="ledger-filter-date-from" className={`block h-4 truncate text-xs font-medium leading-4 ${filters.date_from ? 'text-blue-600' : 'text-gray-600'}`}>
            Date from
          </label>
          <DateInput
            className={getFilterInputClass(filters.date_from)}
            value={filters.date_from}
            onChange={(value) => onFilterChange('date_from', value)}
            dateFormat="iso"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="ledger-filter-date-to" className={`block h-4 truncate text-xs font-medium leading-4 ${filters.date_to ? 'text-blue-600' : 'text-gray-600'}`}>
            Date to
          </label>
          <DateInput
            className={getFilterInputClass(filters.date_to)}
            value={filters.date_to}
            onChange={(value) => onFilterChange('date_to', value)}
            dateFormat="iso"
          />
        </div>
        <MultiSelectFilter
          id="ledger-filter-product"
          label="Product"
          options={productOptions}
          selected={filters.product}
          onChange={(value) => onFilterChange('product', value)}
          searchable
        />
        <MultiSelectFilter
          id="ledger-filter-linked-invoice"
          label="Linked Invoice"
          options={linkedInvoiceOptions}
          selected={filters.linked_invoice}
          onChange={(value) => onFilterChange('linked_invoice', value)}
          searchable
        />
        <MultiSelectFilter
          id="ledger-filter-order-number"
          label="Order #"
          options={orderOptions}
          selected={filters.order_number}
          onChange={(value) => onFilterChange('order_number', value)}
          searchable
        />
        <MultiSelectFilter
          id="ledger-filter-kind"
          label="Type"
          options={kindOptions}
          selected={filters.kind}
          onChange={(value) => onFilterChange('kind', value as WarehouseLedgerKind[])}
          searchable={false}
        />
        <MultiSelectFilter
          id="ledger-filter-signed-cases"
          label="Signed cases"
          options={LEDGER_SIGNED_CASES_FILTER_OPTIONS}
          selected={filters.signed_cases}
          onChange={(value) =>
            onFilterChange('signed_cases', value as LedgerSignedCasesFilterId[])
          }
          searchable={false}
        />
        <div className="filter-bar-amount flex flex-col gap-1">
          <label
            htmlFor="ledger-filter-unit-cost-value"
            className={`text-xs font-medium leading-4 ${hasActiveUnitCostFilter ? 'text-blue-600' : 'text-gray-600'}`}
          >
            Case cost
          </label>
          <div className="filter-bar-amount-controls">
            <select
              id="ledger-filter-unit-cost-op"
              aria-label="Case cost comparison"
              className={
                hasActiveUnitCostFilter
                  ? 'filter-bar-amount-op select ring-2 ring-blue-400 bg-blue-50'
                  : 'filter-bar-amount-op select'
              }
              value={filters.unit_cost_op}
              onChange={(e) =>
                onFilterChange('unit_cost_op', e.target.value as TableAmountFilterOp)
              }
            >
              {TABLE_AMOUNT_FILTER_OPS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <input
              id="ledger-filter-unit-cost-value"
              type="text"
              inputMode="decimal"
              placeholder="0"
              className={`filter-bar-amount-value ${getFilterInputClass(filters.unit_cost)}`}
              value={filters.unit_cost}
              onChange={(e) => onFilterChange('unit_cost', e.target.value)}
            />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="ledger-filter-notes" className={`block h-4 truncate text-xs font-medium leading-4 ${filters.notes ? 'text-blue-600' : 'text-gray-600'}`}>
            Notes
          </label>
          <input
            id="ledger-filter-notes"
            type="text"
            className={getFilterInputClass(filters.notes)}
            value={filters.notes}
            onChange={(e) => onFilterChange('notes', e.target.value)}
          />
        </div>
      </div>
    </div>
  )
}

type WarehouseLedgerTableProps = {
  rows: EditableLedgerRow[]
  skuOptions: { sku: string; product: string; label: string }[]
  skuDisplayNames?: ReadonlyMap<string, string>
  busy?: boolean
  busyTxnIds?: readonly string[]
  invoiceNumberByLineId?: Map<number, string>
  bkTxnFilterRequest?: { bkTxnId: string; nonce: number } | null
  onUpdateRow: (bkTxnId: string, patch: Partial<EditableLedgerRow>) => void
  onJumpToProductInvoice?: (productInvoiceLineId: number) => void
  onVoidRow?: (bkTxnId: string) => void
  onUnvoidRow?: (bkTxnId: string) => void
}

export default function WarehouseLedgerTable({
  rows,
  skuOptions,
  skuDisplayNames,
  busy = false,
  busyTxnIds,
  invoiceNumberByLineId,
  bkTxnFilterRequest,
  onUpdateRow,
  onJumpToProductInvoice,
  onVoidRow,
  onUnvoidRow,
}: WarehouseLedgerTableProps) {
  const [filters, setFilters] = useState<LedgerTableFilters>(() =>
    migrateLedgerTableFilters(
      loadFilterCache(WAREHOUSE_LEDGER_FILTER_CACHE_KEY, EMPTY_LEDGER_TABLE_FILTERS)
    )
  )
  const [overrideRow, setOverrideRow] = useState<WarehouseLedgerTransactionItem | null>(null)
  const [flagsModalRow, setFlagsModalRow] = useState<WarehouseLedgerTransactionItem | null>(null)
  const [contextMenu, setContextMenu] = useState<LedgerRowContextMenuState | null>(null)
  const [pendingVoidTxnIds, setPendingVoidTxnIds] = useState<Set<string>>(() => new Set())
  const [isExporting, setIsExporting] = useState(false)

  const closeContextMenu = useCallback(() => setContextMenu(null), [])

  const busyTxnIdSet = useMemo(() => new Set(busyTxnIds ?? []), [busyTxnIds])

  function isRowVoidBusy(bkTxnId: string): boolean {
    return busy || busyTxnIdSet.has(bkTxnId) || pendingVoidTxnIds.has(bkTxnId)
  }

  function handleRowContextMenu(
    event: MouseEvent<HTMLTableRowElement>,
    row: WarehouseLedgerTransactionItem
  ) {
    const canVoid = !row.voided_at && onVoidRow
    const canUnvoid = row.voided_at && onUnvoidRow
    if (!canVoid && !canUnvoid) return

    event.preventDefault()
    setContextMenu({
      bkTxnId: row.bk_txn_id,
      x: event.clientX,
      y: event.clientY,
    })
  }

  function handleVoidRow(bkTxnId: string) {
    if (!onVoidRow || isRowVoidBusy(bkTxnId)) return
    setPendingVoidTxnIds((prev) => new Set(prev).add(bkTxnId))
    void Promise.resolve(onVoidRow(bkTxnId)).finally(() => {
      setPendingVoidTxnIds((prev) => {
        const next = new Set(prev)
        next.delete(bkTxnId)
        return next
      })
    })
  }

  function handleUnvoidRow(bkTxnId: string) {
    if (!onUnvoidRow || isRowVoidBusy(bkTxnId)) return
    setPendingVoidTxnIds((prev) => new Set(prev).add(bkTxnId))
    void Promise.resolve(onUnvoidRow(bkTxnId)).finally(() => {
      setPendingVoidTxnIds((prev) => {
        const next = new Set(prev)
        next.delete(bkTxnId)
        return next
      })
    })
  }

  useEffect(() => {
    saveFilterCache(WAREHOUSE_LEDGER_FILTER_CACHE_KEY, filters)
  }, [filters])

  useEffect(() => {
    if (!bkTxnFilterRequest?.bkTxnId) return
    setFilters({
      ...EMPTY_LEDGER_TABLE_FILTERS,
      bk_txn_id: [bkTxnFilterRequest.bkTxnId],
    })
  }, [bkTxnFilterRequest?.nonce, bkTxnFilterRequest?.bkTxnId])

  useEffect(() => {
    migrateWarehouseLedgerColumnWidths()
  }, [])

  const { getWidth, startResize, totalWidth, getStickyLeft } = useColumnWidths({
    pageKey: WAREHOUSE_LEDGER_PAGE_KEY,
    columnKeys: [...WAREHOUSE_LEDGER_COLUMN_KEYS],
    defaults: WAREHOUSE_LEDGER_DEFAULT_WIDTHS,
  })

  const pinnedKeys = useMemo(() => [...WAREHOUSE_LEDGER_PINNED_COLUMNS], [])

  const tableStyle = useMemo(
    (): CSSProperties => ({
      width: totalWidth,
      minWidth: totalWidth,
      tableLayout: 'fixed',
    }),
    [totalWidth]
  )

  const headerStyle = useCallback(
    (columnKey: string): CSSProperties => {
      const width = getWidth(columnKey)
      const isPinned = isPinnedLedgerColumn(columnKey)
      const style: CSSProperties = {
        ...fixedColumnStyle(width),
        position: 'sticky',
        top: 0,
        zIndex: isPinned ? 20 : 10,
      }
      if (isPinned) {
        style.left = getStickyLeft(pinnedKeys, columnKey)
      }
      return style
    },
    [getStickyLeft, getWidth, pinnedKeys]
  )

  const cellStyle = useCallback(
    (columnKey: string): CSSProperties => {
      const width = getWidth(columnKey)
      const style: CSSProperties = fixedColumnStyle(width)
      if (isPinnedLedgerColumn(columnKey)) {
        return {
          ...style,
          position: 'sticky',
          left: getStickyLeft(pinnedKeys, columnKey),
          zIndex: 5,
        }
      }
      return style
    },
    [getStickyLeft, getWidth, pinnedKeys]
  )

  const filteredRows = useMemo(
    () => applyLedgerFilters(rows, filters, { invoiceNumberByLineId }),
    [rows, filters, invoiceNumberByLineId]
  )
  const hasActiveFilters = hasActiveLedgerTableFilters(filters)

  const { sortedData, requestSort, getSortDirection } = useSortableTable(
    filteredRows,
    { key: 'bs_log_id', direction: 'asc' },
    ledgerSortValue
  )

  function handleFilterChange<K extends keyof LedgerTableFilters>(
    key: K,
    value: LedgerTableFilters[K]
  ) {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  function getFilterInputClass(value: string): string {
    const baseClass = 'input-sm box-border h-[1.875rem]'
    return value ? `${baseClass} ring-2 ring-blue-400 bg-blue-50` : baseClass
  }

  function buildLedgerExportMatrix(): { headers: string[]; rows: string[][] } {
    const headers = [
      'Flags',
      'BK Txn ID',
      'BS Log ID',
      'Date',
      'BK SKU',
      'Product',
      'Linked invoice',
      'Order number',
      'Kind',
      'Store',
      'Signed cases',
      'Balance',
      'Unit cost',
      'Shipping alloc',
      'Pick fee alloc',
      'Receiving alloc',
      'Notes',
      'Voided',
    ]

    const exportRows = sortedData.map((row) => {
      const canonicalSku = normalizeBulkSku(row.bk_sku)
      const linkedInvoice =
        row.product_invoice_line_id != null
          ? invoiceNumberByLineId?.get(row.product_invoice_line_id) ??
            String(row.product_invoice_line_id)
          : ''
      return [
        ledgerExportFlags(row),
        row.bk_txn_id,
        row.bs_log_id != null ? String(row.bs_log_id) : '',
        row.txn_date,
        canonicalSku,
        ledgerProductLabel(
          canonicalSku,
          productForSku(canonicalSku, skuDisplayNames, row.product_name)
        ),
        linkedInvoice,
        row.order_number ?? '',
        ledgerKindLabel(row.kind),
        row.store ?? '',
        String(row.signed_cases),
        String(row.balance),
        row.unit_cost != null ? String(row.unit_cost) : '',
        String(row.shipping_alloc),
        String(row.pick_fee_alloc),
        String(row.receiving_alloc),
        row.notes ?? '',
        row.voided_at ?? '',
      ]
    })

    return { headers, rows: exportRows }
  }

  function exportLedgerCsv() {
    const { headers, rows: exportRows } = buildLedgerExportMatrix()
    const csvRows = exportRows.map((row) => row.map(escapeCsvCell).join(','))
    downloadCsvFile('warehouse-ledger.csv', [headers.join(','), ...csvRows].join('\n'))
  }

  async function exportLedgerToGoogleSheet() {
    if (sortedData.length === 0 || isExporting) return
    setIsExporting(true)
    try {
      const { headers, rows: exportRows } = buildLedgerExportMatrix()
      const result = await exportWarehouseLedgerSheet({
        headers,
        rows: exportRows,
      })
      toast.success(`Created Google Sheet: ${result.name}`)
      window.open(result.url, '_blank', 'noopener,noreferrer')
    } catch (err: unknown) {
      toast.error(formatApiError(err, 'Failed to export ledger to Google Sheet'))
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <>
      <LedgerFiltersBar
        rows={rows}
        filters={filters}
        hasActiveFilters={hasActiveFilters}
        invoiceNumberByLineId={invoiceNumberByLineId}
        skuDisplayNames={skuDisplayNames}
        onFilterChange={handleFilterChange}
        onClearFilters={() => setFilters(EMPTY_LEDGER_TABLE_FILTERS)}
        getFilterInputClass={getFilterInputClass}
      />

      {rows.length > 0 ? (
        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="min-w-0 truncate text-xs sm:text-sm text-gray-500">
            {hasActiveFilters ? (
              <>Showing {filteredRows.length} of {rows.length} (filtered)</>
            ) : (
              <>
                Showing {filteredRows.length} transaction{filteredRows.length !== 1 ? 's' : ''}
              </>
            )}
          </p>
          <TableToolbar
            exportOptions={[
              {
                id: 'sheets',
                label: 'Google Sheet',
                onClick: () => void exportLedgerToGoogleSheet(),
                disabled: sortedData.length === 0,
              },
              {
                id: 'csv',
                label: 'CSV',
                onClick: exportLedgerCsv,
                disabled: sortedData.length === 0,
              },
            ]}
            defaultExportId="sheets"
            isExporting={isExporting}
            exportDisabled={sortedData.length === 0}
          />
        </div>
      ) : null}

      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-gray-500">No ledger transactions yet.</p>
      ) : filteredRows.length === 0 ? (
        <p className="mt-4 text-sm text-gray-500">No rows match the current filters.</p>
      ) : (
        <div className="table-scroll-mobile mt-4 min-h-0 flex-1 overflow-x-auto overflow-y-auto rounded-lg border border-gray-100">
          <table
            className="table-forecast border-separate border-spacing-0"
            style={tableStyle}
          >
            <thead>
              <tr>
                <th
                  className={`relative ${pinnedLedgerHeaderClass('status')}`}
                  style={headerStyle('status')}
                >
                  <HoverHelp label="Flags" aria-label="Flags column help">
                    Override drift (grey) and data-quality warnings (red). Click the grey pencil for
                    override details; click red or reviewed icons to manage flags.
                  </HoverHelp>
                  <ColumnResizeHandle columnKey="status" onStartResize={startResize} />
                </th>
                <LedgerSortableHeader
                  label={
                    <HoverHelp label="BK Txn ID" aria-label="BK Txn ID column help">
                      Stable Bookkeeper transaction identity.
                    </HoverHelp>
                  }
                  sortKey="bk_txn_id"
                  sortDirection={getSortDirection('bk_txn_id')}
                  onSort={requestSort}
                  className={pinnedLedgerHeaderClass('bk_txn_id')}
                  columnKey="bk_txn_id"
                  onStartResize={startResize}
                  style={headerStyle('bk_txn_id')}
                />
                <LedgerSortableHeader
                  label={
                    <HoverHelp label="BS Log ID" aria-label="BS Log ID column help">
                      Bright Sites inventory log ID when sourced from BS.
                    </HoverHelp>
                  }
                  sortKey="bs_log_id"
                  sortDirection={getSortDirection('bs_log_id')}
                  onSort={requestSort}
                  className={pinnedLedgerHeaderClass('bs_log_id')}
                  columnKey="bs_log_id"
                  onStartResize={startResize}
                  style={headerStyle('bs_log_id')}
                />
                <LedgerSortableHeader
                  label={
                    <HoverHelp label="Date" aria-label="Date column help">
                      Transaction event date.
                    </HoverHelp>
                  }
                  sortKey="txn_date"
                  sortDirection={getSortDirection('txn_date')}
                  onSort={requestSort}
                  className={pinnedLedgerHeaderClass('txn_date')}
                  columnKey="txn_date"
                  onStartResize={startResize}
                  style={headerStyle('txn_date')}
                />
                <LedgerSortableHeader
                  label={
                    <HoverHelp label="Product" aria-label="Product column help">
                      Canonical BK SKU (WH prefix normalized to BK).
                    </HoverHelp>
                  }
                  sortKey="bk_sku"
                  sortDirection={getSortDirection('bk_sku')}
                  onSort={requestSort}
                  className={pinnedLedgerHeaderClass('bk_sku')}
                  columnKey="bk_sku"
                  onStartResize={startResize}
                  style={headerStyle('bk_sku')}
                />
                <LedgerSortableHeader
                  label={
                    <HoverHelp label="Linked Invoice" aria-label="Linked invoice column help">
                      Product invoice line linked to this transaction. Click to open on Uploads.
                    </HoverHelp>
                  }
                  sortKey="product_invoice_line_id"
                  sortDirection={getSortDirection('product_invoice_line_id')}
                  onSort={requestSort}
                  columnKey="linked_invoice"
                  onStartResize={startResize}
                  style={headerStyle('linked_invoice')}
                />
                <LedgerSortableHeader
                  label={
                    <HoverHelp label="Order #" aria-label="Order number column help">
                      Bright Sites or warehouse order number.
                    </HoverHelp>
                  }
                  sortKey="order_number"
                  sortDirection={getSortDirection('order_number')}
                  onSort={requestSort}
                  columnKey="order_number"
                  onStartResize={startResize}
                  style={headerStyle('order_number')}
                />
                <LedgerSortableHeader
                  label={
                    <HoverHelp label="Type" aria-label="Type column help">
                      Transaction type: store transfer, manufacturer purchase, store draw, or manual
                      adjustment.
                    </HoverHelp>
                  }
                  sortKey="kind"
                  sortDirection={getSortDirection('kind')}
                  onSort={requestSort}
                  columnKey="kind"
                  onStartResize={startResize}
                  style={headerStyle('kind')}
                />
                <LedgerSortableHeader
                  label={
                    <HoverHelp label="Store" aria-label="Store column help">
                      Store transfer: sending store. Store draw: receiving store (usually from
                      billing).
                    </HoverHelp>
                  }
                  sortKey="store"
                  sortDirection={getSortDirection('store')}
                  onSort={requestSort}
                  columnKey="store"
                  onStartResize={startResize}
                  style={headerStyle('store')}
                />
                <LedgerSortableHeader
                  label={
                    <HoverHelp label="Signed cases" aria-label="Signed cases column help">
                      Cases into warehouse (+) or out to stores (−).
                    </HoverHelp>
                  }
                  sortKey="signed_cases"
                  sortDirection={getSortDirection('signed_cases')}
                  onSort={requestSort}
                  columnKey="signed_cases"
                  onStartResize={startResize}
                  style={headerStyle('signed_cases')}
                />
                <LedgerSortableHeader
                  label={
                    <HoverHelp label="Balance" aria-label="Balance column help">
                      Running on-hand cases for this SKU through this transaction.
                    </HoverHelp>
                  }
                  sortKey="balance"
                  sortDirection={getSortDirection('balance')}
                  onSort={requestSort}
                  columnKey="balance"
                  onStartResize={startResize}
                  style={headerStyle('balance')}
                />
                <LedgerSortableHeader
                  label={
                    <HoverHelp label="Case cost" aria-label="Case cost column help">
                      After-tax cost per case when known. Edits create field overrides.
                    </HoverHelp>
                  }
                  sortKey="unit_cost"
                  sortDirection={getSortDirection('unit_cost')}
                  onSort={requestSort}
                  columnKey="unit_cost"
                  onStartResize={startResize}
                  style={headerStyle('unit_cost')}
                />
                <LedgerSortableHeader
                  label={
                    <HoverHelp label="Shipping alloc" aria-label="Shipping alloc column help">
                      Shared shipping allocated to this transaction (read-only until uploads).
                    </HoverHelp>
                  }
                  sortKey="shipping_alloc"
                  sortDirection={getSortDirection('shipping_alloc')}
                  onSort={requestSort}
                  columnKey="shipping_alloc"
                  onStartResize={startResize}
                  style={headerStyle('shipping_alloc')}
                />
                <LedgerSortableHeader
                  label={
                    <HoverHelp label="Pick fee alloc" aria-label="Pick fee alloc column help">
                      Shared pick fee allocated to this transaction (read-only until uploads).
                    </HoverHelp>
                  }
                  sortKey="pick_fee_alloc"
                  sortDirection={getSortDirection('pick_fee_alloc')}
                  onSort={requestSort}
                  columnKey="pick_fee_alloc"
                  onStartResize={startResize}
                  style={headerStyle('pick_fee_alloc')}
                />
                <LedgerSortableHeader
                  label={
                    <HoverHelp label="Receiving alloc" aria-label="Receiving alloc column help">
                      Shared receiving fee allocated to inbound transactions (read-only until uploads).
                    </HoverHelp>
                  }
                  sortKey="receiving_alloc"
                  sortDirection={getSortDirection('receiving_alloc')}
                  onSort={requestSort}
                  columnKey="receiving_alloc"
                  onStartResize={startResize}
                  style={headerStyle('receiving_alloc')}
                />
                <LedgerSortableHeader
                  label={
                    <HoverHelp label="Notes" aria-label="Notes column help">
                      Free-form notes for this transaction.
                    </HoverHelp>
                  }
                  sortKey="notes"
                  sortDirection={getSortDirection('notes')}
                  onSort={requestSort}
                  columnKey="notes"
                  onStartResize={startResize}
                  style={headerStyle('notes')}
                />
              </tr>
            </thead>
            <tbody>
              {sortedData.map((row) => {
                const rowClass = row.dirty
                  ? 'table-row-draft bg-sky-50'
                  : row.voided_at
                    ? 'table-row-voided bg-gray-50 text-gray-500'
                    : undefined
                const canonicalSku = normalizeBulkSku(row.bk_sku)
                const orderLocked = isLedgerFieldLocked(row.kind, 'order_number', row)
                const unitCostLocked = isLedgerFieldLocked(row.kind, 'unit_cost', row)
                const shippingLocked = isLedgerFieldLocked(row.kind, 'shipping_alloc', row)
                const pickFeeLocked = isLedgerFieldLocked(row.kind, 'pick_fee_alloc', row)
                const receivingLocked = isLedgerFieldLocked(row.kind, 'receiving_alloc', row)
                const storeLocked = isLedgerFieldLocked(row.kind, 'store', row)
                const storeRequiredEmpty =
                  row.kind === 'initial_store_transfer' && !storeLocked && !row.store?.trim()

                return (
                  <tr
                    key={row.bk_txn_id}
                    className={`${rowClass ?? ''} [&>td]:align-middle [&>td]:!py-1 [&>td]:!px-1.5`.trim()}
                    onContextMenu={(event) => handleRowContextMenu(event, row)}
                  >
                    <td className={pinnedLedgerCellClass('status')} style={cellStyle('status')}>
                      <div className={LEDGER_READONLY_CELL}>
                        <LedgerStatusChip
                          row={row}
                          onOpenFlags={() => setFlagsModalRow(row)}
                          onOpenOverride={() => setOverrideRow(row)}
                        />
                      </div>
                    </td>
                    <td
                      className={`${pinnedLedgerCellClass('bk_txn_id')} text-gray-700`}
                      style={cellStyle('bk_txn_id')}
                      title={row.bk_txn_id}
                    >
                      <div className={LEDGER_READONLY_CELL}>{row.bk_txn_id.slice(0, 8)}</div>
                    </td>
                    <td
                      className={`${pinnedLedgerCellClass('bs_log_id')} tabular-nums text-gray-700`}
                      style={cellStyle('bs_log_id')}
                    >
                      <div className={LEDGER_READONLY_CELL}>{row.bs_log_id ?? '—'}</div>
                    </td>
                    <td
                      className={pinnedLedgerCellClass('txn_date')}
                      style={cellStyle('txn_date')}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="date"
                        className={cellInputClass(false)}
                        value={row.txn_date}
                        disabled={busy}
                        onChange={(e) =>
                          onUpdateRow(row.bk_txn_id, { txn_date: e.target.value })
                        }
                      />
                    </td>
                    <td
                      className={pinnedLedgerCellClass('bk_sku')}
                      style={cellStyle('bk_sku')}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FindableSelect
                        className={`${cellInputClass(false)} font-medium text-gray-900`}
                        value={canonicalSku}
                        disabled={busy}
                        aria-label="Product"
                        findText={
                          canonicalSku
                            ? ledgerProductLabel(
                                canonicalSku,
                                productForSku(canonicalSku, skuDisplayNames, row.product_name)
                              )
                            : ''
                        }
                        onChange={(e) => {
                          const sku = e.target.value
                          const option = skuOptions.find((item) => item.sku === sku)
                          onUpdateRow(row.bk_txn_id, {
                            bk_sku: sku,
                            product_name: option?.product ?? sku,
                          })
                        }}
                      >
                        <option value="">Select SKU…</option>
                        {skuOptions.map((option) => (
                          <option key={option.sku} value={option.sku}>
                            {option.label}
                          </option>
                        ))}
                        {canonicalSku &&
                        !skuOptions.some((option) => option.sku === canonicalSku) ? (
                          <option value={canonicalSku}>
                            {ledgerProductLabel(
                              canonicalSku,
                              productForSku(canonicalSku, skuDisplayNames, row.product_name)
                            )}
                          </option>
                        ) : null}
                      </FindableSelect>
                    </td>
                    <td
                      className="max-w-0"
                      style={cellStyle('linked_invoice')}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <LinkedInvoiceCell
                        row={row}
                        invoiceNumberByLineId={invoiceNumberByLineId}
                        onJumpToProductInvoice={onJumpToProductInvoice}
                      />
                    </td>
                    <td style={cellStyle('order_number')} onClick={(e) => e.stopPropagation()}>
                      <input
                        className={cellInputClass(orderLocked)}
                        value={row.order_number ?? ''}
                        disabled={busy || orderLocked}
                        readOnly={orderLocked}
                        onChange={(e) =>
                          onUpdateRow(row.bk_txn_id, {
                            order_number: e.target.value || null,
                          })
                        }
                      />
                      {row.kind === 'store_return' && row.linked_return_of_bk_txn_id ? (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          returns draw #{row.order_number ?? '—'}
                        </div>
                      ) : null}
                    </td>
                    <td style={cellStyle('kind')} onClick={(e) => e.stopPropagation()}>
                      <FindableSelect
                        className={cellInputClass(false)}
                        value={row.kind}
                        disabled={busy}
                        aria-label="Type"
                        findText={ledgerKindLabel(row.kind)}
                        onChange={(e) =>
                          onUpdateRow(row.bk_txn_id, {
                            kind: e.target.value as WarehouseLedgerKind,
                          })
                        }
                      >
                        {LEDGER_KIND_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </FindableSelect>
                    </td>
                    <td style={cellStyle('store')} onClick={(e) => e.stopPropagation()}>
                      <FindableSelect
                        className={cellInputClass(storeLocked, storeRequiredEmpty)}
                        value={row.store ?? ''}
                        disabled={busy || storeLocked}
                        aria-label="Store"
                        findText={row.store ?? ''}
                        onChange={(e) =>
                          onUpdateRow(row.bk_txn_id, {
                            store: e.target.value || null,
                          })
                        }
                      >
                        <option value="">
                          {storeRequiredEmpty ? 'Select store…' : '—'}
                        </option>
                        {LEDGER_STORE_LOCATIONS.map((location) => (
                          <option key={location} value={location}>
                            {location}
                          </option>
                        ))}
                        {row.store &&
                        !LEDGER_STORE_LOCATIONS.includes(
                          row.store as (typeof LEDGER_STORE_LOCATIONS)[number]
                        ) ? (
                          <option value={row.store}>{row.store}</option>
                        ) : null}
                      </FindableSelect>
                    </td>
                    <td style={cellStyle('signed_cases')} onClick={(e) => e.stopPropagation()}>
                      <SignedCasesInput
                        value={row.signed_cases}
                        busy={busy}
                        onCommit={(next) =>
                          onUpdateRow(row.bk_txn_id, { signed_cases: next })
                        }
                      />
                    </td>
                    <td
                      className={`tabular-nums ${
                        Number(row.balance) < 0
                          ? 'font-medium text-red-700'
                          : Number(row.balance) > 0
                            ? 'font-medium text-emerald-700'
                            : 'text-gray-700'
                      }`}
                      style={cellStyle('balance')}
                    >
                      <div className={LEDGER_READONLY_CELL}>
                        {formatSignedCasesDisplay(Number(row.balance) || 0)}
                      </div>
                    </td>
                    <td style={cellStyle('unit_cost')} onClick={(e) => e.stopPropagation()}>
                      <LedgerMoneyInput
                        value={row.unit_cost}
                        busy={busy}
                        locked={unitCostLocked}
                        emptyClears
                        onCommit={(next) => onUpdateRow(row.bk_txn_id, { unit_cost: next })}
                      />
                    </td>
                    <td style={cellStyle('shipping_alloc')} onClick={(e) => e.stopPropagation()}>
                      <LedgerMoneyInput
                        value={row.shipping_alloc}
                        busy={busy}
                        locked={shippingLocked}
                        onCommit={(next) =>
                          onUpdateRow(row.bk_txn_id, { shipping_alloc: next ?? 0 })
                        }
                      />
                    </td>
                    <td style={cellStyle('pick_fee_alloc')} onClick={(e) => e.stopPropagation()}>
                      <LedgerMoneyInput
                        value={row.pick_fee_alloc}
                        busy={busy}
                        locked={pickFeeLocked}
                        onCommit={(next) =>
                          onUpdateRow(row.bk_txn_id, { pick_fee_alloc: next ?? 0 })
                        }
                      />
                    </td>
                    <td style={cellStyle('receiving_alloc')} onClick={(e) => e.stopPropagation()}>
                      <LedgerMoneyInput
                        value={row.receiving_alloc}
                        busy={busy}
                        locked={receivingLocked}
                        onCommit={(next) =>
                          onUpdateRow(row.bk_txn_id, { receiving_alloc: next ?? 0 })
                        }
                      />
                    </td>
                    <td style={cellStyle('notes')} onClick={(e) => e.stopPropagation()}>
                      <input
                        className={cellInputClass(false)}
                        value={row.notes ?? ''}
                        disabled={busy}
                        onChange={(e) =>
                          onUpdateRow(row.bk_txn_id, { notes: e.target.value || null })
                        }
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {overrideRow ? (
        <WarehouseLedgerOverrideModal row={overrideRow} onClose={() => setOverrideRow(null)} />
      ) : null}

      {flagsModalRow ? (
        <WarehouseLedgerFlagsModal
          row={flagsModalRow}
          onClose={() => setFlagsModalRow(null)}
          onRowUpdated={(updated) => {
            onUpdateRow(updated.bk_txn_id, { ignored_flags: updated.ignored_flags ?? [] })
            setFlagsModalRow(updated)
          }}
        />
      ) : null}

      {contextMenu ? (() => {
        const menuRow = sortedData.find((row) => row.bk_txn_id === contextMenu.bkTxnId)
        if (!menuRow) return null
        return (
          <LedgerRowContextMenu
            row={menuRow}
            position={{ x: contextMenu.x, y: contextMenu.y }}
            busy={isRowVoidBusy(menuRow.bk_txn_id)}
            onClose={closeContextMenu}
            onVoidRow={handleVoidRow}
            onUnvoidRow={handleUnvoidRow}
          />
        )
      })() : null}
    </>
  )
}
